import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { formatText } from '../../common/TextFormatterProvider';
import { getPeriodType, PERIOD_CONFIG, formatPeriodKey } from '../../../utils/periodUtils';
import { mandatoryScopeLabel } from './TableSchemaViewer';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1),
  '&:last-child': {
    borderRight: 'none',
  },
  backgroundColor: theme.palette.background.paper,
}));

const HeaderCell = styled(StyledTableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.dark,
  fontWeight: 'bold',
}));

function TableSchemaPreview({ schema }) {
  const isRaw = schema?.tableType === 'raw';
  const isNestedValue = (value) => {
    return typeof value === 'object' && value.type === undefined;
  };

  const processSchema = () => {
    const columns = [];
    const headerRows = [[], [], []]; // Up to 3 rows for headers
    // One example cell per leaf column, in header order: every static column,
    // then every value leaf.
    let valueLeafCount = 0;

    // Add row identifier. `mandatory` names a column's requirement: the
    // identifier's and an attribute's upload-tag requirement, a detail column's
    // or a metric leaf's scopes (Spec 6 section 2.2), '' when it has none.
    if (schema.identifier?.name) {
      columns.push({
        header: schema.identifier.name,
        type: schema.identifier.type,
        isIdentifier: true,
        mandatory: 'mandatory'
      });
    }

    // Add attribute columns
    schema.attributeColumns?.forEach(col => {
      columns.push({
        header: col.name,
        type: col.type,
        isAttribute: true,
        mandatory: col.mandatory ? 'mandatory' : ''
      });
    });

    // Add table attribute columns
    schema.tableAttributeColumns?.forEach(col => {
      columns.push({
        header: col.name,
        type: col.type,
        value: col.value,
        isTableAttribute: true
      });
    });

    // Add detail columns
    schema.detailColumns?.forEach(col => {
      columns.push({
        header: col.name,
        type: col.type,
        isDetail: true,
        isRawColumn: isRaw,
        mandatory: mandatoryScopeLabel(col)
      });
    });

    // Process value columns if they exist (a time-series table's only)
    if (schema.metricColumns) {
      const valueSchema = schema.metricColumns.schema;
      let totalColumns = 0;

      // Calculate total columns needed for dynamic part
      Object.entries(valueSchema).forEach(([key, value]) => {
        if (isNestedValue(value)) {
          totalColumns += Object.keys(value).length;
        } else {
          totalColumns += 1;
        }
      });

      // Get the period type and generate an example period header
      const periodType = getPeriodType(schema);
      const periodConfig = PERIOD_CONFIG[periodType];
      // Use the formatted example period key for display
      const examplePeriodDisplay = formatPeriodKey(periodConfig?.example || '2024_01');

      // Add period header spanning all value columns
      headerRows[0].push({
        content: examplePeriodDisplay,
        colSpan: totalColumns,
        rowSpan: 1,
        isDynamic: true
      });

      // Process each value column
      Object.entries(valueSchema).forEach(([key, value]) => {
        valueLeafCount += isNestedValue(value) ? Object.keys(value).length : 1;
        if (isNestedValue(value)) {
          // Nested structure (2 layers)
          headerRows[1].push({
            content: formatText(key),
            colSpan: Object.keys(value).length,
            rowSpan: 1
          });

          // Add each nested value
          Object.entries(value).forEach(([nestedKey, nestedValue]) => {
            headerRows[2].push({
              content: formatText(nestedKey),
              type: nestedValue.type,
              updateBehavior: nestedValue.updateBehavior,
              mandatory: mandatoryScopeLabel(nestedValue),
              colSpan: 1,
              rowSpan: 1
            });
          });
        } else {
          // Single layer
          headerRows[1].push({
            content: formatText(key),
            type: value.type,
            updateBehavior: value.updateBehavior,
            mandatory: mandatoryScopeLabel(value),
            colSpan: 1,
            rowSpan: 2
          });
        }
      });
    }

    // The static columns lead the first header row in the order they were
    // defined: identifier, attributes, table attributes, details.
    headerRows[0] = [
      ...columns.map((col) => ({
        content: formatText(col.header),
        type: col.type,
        colSpan: 1,
        rowSpan: 3,
        isIdentifier: col.isIdentifier,
        isAttribute: col.isAttribute,
        isTableAttribute: col.isTableAttribute,
        tableAttributeValue: col.value,
        isDetail: col.isDetail,
        isRawColumn: col.isRawColumn,
        mandatory: col.mandatory
      })),
      ...headerRows[0],
    ];

    return { headerRows, leafCount: columns.length + valueLeafCount };
  };

  const { headerRows, leafCount } = processSchema();

  const renderHeaderCell = (cell, rowIndex, cellIndex) => {
    if (!cell) return null;

    let subtitle = '';
    let mainContent = cell.content;

    // What every upload must carry of the column (M224).
    const mandatory = cell.mandatory ? `, ${cell.mandatory}` : '';
    if (cell.type) {
      if (cell.isIdentifier) {
        subtitle = `Identifier (${cell.type}${mandatory})`;
      } else if (cell.isTableAttribute) {
        subtitle = `Table Attribute (${cell.type}) = ${cell.tableAttributeValue}`;
      } else if (cell.isAttribute) {
        subtitle = `Attribute (${cell.type}${mandatory})`;
      } else if (cell.isDetail) {
        subtitle = `${cell.isRawColumn ? 'Column' : 'Detail'} (${cell.type}${mandatory})`;
      } else if (cell.updateBehavior) {
        // For value columns, include type and update behavior in main content
        mainContent = `${cell.content} (${cell.type}, ${cell.updateBehavior}${mandatory})`;
      }
    }

    return (
      <HeaderCell
        key={`${rowIndex}-${cellIndex}`}
        align="center"
        colSpan={cell.colSpan}
        rowSpan={cell.rowSpan}
        sx={{
          backgroundColor: cell.isDynamic ? "primary.dark" : undefined,
          minWidth: '120px'
        }}
      >
        <Typography 
          variant="subtitle2" 
          component="div"
          sx={{ 
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {mainContent}
        </Typography>
        {subtitle && (
          <Typography 
            variant="caption" 
            component="div" 
            color="textSecondary"
            sx={{ 
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {subtitle}
          </Typography>
        )}
      </HeaderCell>
    );
  };

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          {headerRows.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {row.map((cell, cellIndex) => renderHeaderCell(cell, rowIndex, cellIndex))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          <TableRow>
            {Array.from({ length: leafCount }, (_, index) => (
              <StyledTableCell key={index} align="center">
                <Typography variant="caption" color="textSecondary">
                  Example Data
                </Typography>
              </StyledTableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default TableSchemaPreview;
